import { isNaN } from 'lodash-es';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { Task, TaskAllocation, User } from '@/type';

import { getTaskFrameInfo } from '../../../Task/MemberTask/util';

type FormData = {
  taskAllocations: TaskAllocation[];
  unAssignedUserID: User['id'][];
};

export const useRenderedFrameList = (task: Task) => {
  const { watch, register, trigger, formState } = useFormContext<FormData>();
  const { errors } = formState;
  const { t } = useTranslation();
  const { startFrameSeq, endFrameSeq } = getTaskFrameInfo(task);

  const unAssignedUserIDList = watch('unAssignedUserID');

  const renderedFrameList = (() => {
    const userList = task.userTask ?? [];
    return userList.map((ut, index) => {
      return {
        ...ut,
        id: ut.userID,
        index,
        name: ut.user?.name || ut.user?.email.split('@').at(0) || '-',
        inProgressFrame: ut.inProgressFrameCount + ut.rejectedFrameCount,
        completedFrame:
          ut.submittedFrameCount +
          ut.inReviewFrameCount +
          ut.completedFrameCount,
        startFrame: null,
        endFrame: null,
        unassigned: unAssignedUserIDList.includes(ut.userID),

        registerStartFrame: (() => {
          const registration = register(
            `taskAllocations.${index}.allocateRange.0`,
            {
              valueAsNumber: true,
              validate: value => {
                const currentUnAssignedList = watch('unAssignedUserID');
                if (currentUnAssignedList.includes(ut.userID)) return true;

                if (!value || isNaN(value))
                  return t('task.distributeValueError.aurothiosulphuric');

                const endValue = watch(
                  `taskAllocations.${index}.allocateRange.1`,
                );
                if (endValue && value > endValue) {
                  return t('task.distributeEndError.gloriation');
                }
                if (value < startFrameSeq || value > endFrameSeq)
                  return t('task.distributeRangeError.deserve', {
                    taskStartFrame: startFrameSeq,
                    taskEndFrame: endFrameSeq,
                  });
              },
            },
          );
          return {
            ...registration,
            onChange: async (e: React.ChangeEvent<HTMLInputElement>) => {
              await registration.onChange(e);
              await trigger(`taskAllocations.${index}.allocateRange.1`);
            },
          };
        })(),
        isValidStartFrame: !errors.taskAllocations?.[index]?.allocateRange?.[0],
        registerEndFrame: (() => {
          const registration = register(
            `taskAllocations.${index}.allocateRange.1`,
            {
              valueAsNumber: true,
              validate: value => {
                // unassigned인 경우 validation 건너뛰기
                const currentUnAssignedList = watch('unAssignedUserID');
                if (currentUnAssignedList.includes(ut.userID)) return true;

                if (!value || isNaN(value))
                  return t('task.distributeValueError.aurothiosulphuric');

                const startValue = watch(
                  `taskAllocations.${index}.allocateRange.0`,
                );
                if (startValue && value < startValue) {
                  return t('task.distiributeStartError.trocheameter');
                }
                if (value < startFrameSeq || value > endFrameSeq)
                  return t('task.distributeRangeError.deserve', {
                    taskStartFrame: startFrameSeq,
                    taskEndFrame: endFrameSeq,
                  });
              },
            },
          );
          return {
            ...registration,
            onChange: async (e: React.ChangeEvent<HTMLInputElement>) => {
              await registration.onChange(e);
              await trigger(`taskAllocations.${index}.allocateRange.0`);
            },
          };
        })(),
        isValidEndFrame: !errors.taskAllocations?.[index]?.allocateRange?.[1],
      };
    });
  })();

  return { renderedFrameList };
};
